import { $t } from '@/i18n';
import { useEnvOptions } from '@/workbench/components/preview-button/useTagEnvOptions';
import { ENV_TAG_SEPARATOR } from '@/workbench/pages/deployment/publish-setting/components/useEnvTag';
import { computed, defineComponent, ref, watch } from '@vue/composition-api';
import classNames from 'classnames';
import Fuse from 'fuse.js';
import { isMobileOrNarrowScreen } from '@/utils/screen';
import styles from './tag-env-select.module.scss';
function collectLeaves(opts) {
    const leaves = [];
    for (const option of opts) {
        if (option.children?.length) {
            leaves.push(...collectLeaves(option.children));
        }
        else {
            leaves.push(option);
        }
    }
    return leaves;
}
function rebuildTree(opts, matchedSet) {
    const result = [];
    for (const option of opts) {
        if (option.children?.length) {
            const filteredChildren = rebuildTree(option.children, matchedSet);
            if (filteredChildren.length) {
                result.push({ ...option, children: filteredChildren });
            }
        }
        else if (matchedSet.has(option.value)) {
            result.push(option);
        }
    }
    return result;
}
const FUSE_SEARCH_KEYS = ['content', 'env.name', 'env.env'];
const FUSE_THRESHOLD = 0.4;
function filterOptionsTree(opts, keyword, fuse) {
    const k = keyword.trim();
    if (!k) {
        return opts;
    }
    const matched = fuse.search(k);
    const matchedSet = new Set(matched.map(r => r.item.value));
    return rebuildTree(opts, matchedSet);
}
export const TagEnvSelect = defineComponent({
    name: 'RunTimeTagEnvSelect',
    props: {
        envList: {
            type: Array,
            required: true,
        },
        theme: {
            type: String,
        },
        env: {
            type: Object,
        },
        visible: {
            type: Boolean,
        },
    },
    emits: {
        change: (_env) => void 0,
        'update:visible': (_visible) => void 0,
    },
    setup(props, { emit }) {
        const { options, hasTagEnv, taggedEnvs, nonTaggedEnvs, envToOption } = useEnvOptions(() => props.envList);
        const searchKeyword = ref('');
        const isMobile = computed(isMobileOrNarrowScreen);
        const fuseIndex = computed(() => {
            const leaves = collectLeaves(options.value);
            return new Fuse(leaves, {
                keys: FUSE_SEARCH_KEYS,
                threshold: FUSE_THRESHOLD,
                ignoreLocation: true,
            });
        });
        const filteredOptions = computed(() => {
            if (isMobile.value) {
                return options.value;
            }
            return filterOptionsTree(options.value, searchKeyword.value, fuseIndex.value);
        });
        const envPaths = computed(() => {
            const items = (props.env?.tag?.split(ENV_TAG_SEPARATOR) ?? []).filter(Boolean);
            const paths = [];
            if (items.length) {
                paths.push(items.reduce((prev, cur) => {
                    paths.push(prev);
                    const merged = [prev, cur].join(ENV_TAG_SEPARATOR);
                    return merged;
                }));
            }
            return paths;
        });
        const resetCls = computed(() => classNames(styles.styleReset, props.theme));
        const overlayClassName = computed(() => classNames(`env-select-popover ${props.theme} ${resetCls.value}`, styles.popover, {
            [styles.hasTag]: hasTagEnv.value,
        }));
        const isSelected = (option) => {
            const { env } = props;
            if (!env?.env) {
                return false;
            }
            const isTarget = !option.children?.length && option.env?.env === env?.env;
            const isPath = option.children?.length && envPaths.value.includes(option.value);
            return Boolean(isTarget || isPath);
        };
        const getDataActive = (option) => +isSelected(option);
        const renderFolder = (option) => (<a-menu data-active={getDataActive(option)} theme={props.theme}>
        <a-sub-menu popupClassName={classNames(resetCls.value)} key={option.value} title={option.content}>
          {option.children?.map(renderSubMenu)}
        </a-sub-menu>
      </a-menu>);
        const renderItem = (option) => (<p onClick={() => emit('change', option.env)} data-active={getDataActive(option)} class={classNames('env-select_options', {
                ['env-select_options-active']: isSelected(option),
            })} key={option.value}>
        {option.content}
      </p>);
        const renderSubMenu = (option) => {
            if (option.children?.length) {
                return (<a-sub-menu data-active={getDataActive(option)} popupClassName={classNames(resetCls.value)} key={option.value} title={option.content}>
            {option.children.map(renderSubMenu)}
          </a-sub-menu>);
            }
            return <a-menu-item>{renderItem(option)}</a-menu-item>;
        };
        const distributeContentRender = (option) => {
            if (option.children?.length) {
                return renderFolder(option);
            }
            return renderItem(option);
        };
        const contentRender = () => {
            if (isMobile.value) {
                return nonTaggedEnvs.value.concat(taggedEnvs.value).map(env => renderItem(envToOption(env)));
            }
            const list = filteredOptions.value;
            return (<div class={styles.desktopContent}>
          {props.envList.length > 10 ? (<div class={styles.searchInput} onClick={(e) => e.stopPropagation()}>
              <a-input value={searchKeyword.value} placeholder={$t('k_f2c81f4b')} allowClear onChange={(e) => {
                        searchKeyword.value = e.target?.value ?? '';
                    }}>
                <a-icon slot="prefix" type="search"/>
              </a-input>
            </div>) : null}
          {list.length === 0 ? (<div class={styles.emptyTip}>{$t('k_8c98ac18')}</div>) : (list.map(distributeContentRender))}
        </div>);
        };
        const innerVisible = computed({
            get() {
                return props.visible;
            },
            set(val) {
                emit('update:visible', val);
            },
        });
        watch(() => props.visible, (visible) => {
            if (!visible) {
                searchKeyword.value = '';
            }
        });
        return { options, contentRender, overlayClassName, innerVisible };
    },
    render() {
        return (<a-popover v-model={this.innerVisible} overlay-class-name={classNames(this.overlayClassName)} trigger="click" scopedSlots={{
                content: this.contentRender,
            }}>
        {this.$slots.default}
      </a-popover>);
    },
});
